import { SectionName, SubSectionName } from 'enums/route.enums';
import { useNavSections } from 'hooks/useNavSections';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

export type ActiveRoute = {
  section: SectionName | undefined;
  subsection: SubSectionName | undefined;
};

const EMPTY_ROUTE: ActiveRoute = {
  section: undefined,
  subsection: undefined,
};

/** Derive active section/subsection from the URL (single source of truth). */
export const useActiveRoute = (): ActiveRoute => {
  const { userSections, adminSections } = useNavSections();
  const location = useLocation();

  return useMemo(() => {
    // Match the leading segment as the section and capture the rest.
    const sectionMatch = matchPath('/:section/*', location.pathname);
    if (!sectionMatch) {
      return EMPTY_ROUTE; // shouldn't really happen
    }

    // ie. main section identifier (e.g. "/pitches" or "/admin")
    const sectionSlug = `/${sectionMatch.params.section ?? ''}`;
    const sectionDef =
      userSections.find((s) => s.slug === sectionSlug) ??
      adminSections.find((s) => s.slug === sectionSlug);

    if (!sectionDef) {
      return EMPTY_ROUTE; // shouldn't really happen
    }

    // Get the subsection from the URL by splitting the path after the section:
    const afterSection = sectionMatch.params['*'] ?? ''; // e.g. "list/abc123" in "/pitches/list/abc123"
    const nextSegment = afterSection.split('/')[0] ?? ''; // e.g. "list" in "/pitches/list/abc123"

    // Find the subsection definition that matches the next segment.
    const subsectionDef = sectionDef.subsections?.find(
      (ss) => ss.slug === `/${nextSegment}` // e.g. the list subsection
    );

    return {
      section: sectionDef.value,
      subsection: subsectionDef?.value,
    };
  }, [location.pathname, userSections, adminSections]);
};
